import { useMemo, useState } from "react";

import { cn } from "../lib/utils";

const NODES = [
  {
    key: "regulation",
    title: "Regulation",
    subtitle: "RBI / SEBI / CERT-In / IRDAI",
    examples: ["RBI Master Directions", "CERT-In 6-hour reporting"],
  },
  {
    key: "control",
    title: "Control",
    subtitle: "Requirements → testable controls",
    examples: ["MFA for admin access", "5-year log retention"],
  },
  {
    key: "technology",
    title: "Technology",
    subtitle: "Tools that enforce and measure",
    examples: ["CyberArk", "Splunk", "Palo Alto"],
  },
  {
    key: "evidence",
    title: "Evidence",
    subtitle: "Audit-ready artifacts",
    examples: ["Configuration snapshots", "Audit logs", "Policies"],
  },
  {
    key: "dashboard",
    title: "Dashboard",
    subtitle: "Continuous posture + RAG",
    examples: ["Compliance %", "Risk score", "Gaps"],
  },
];

export function ComplianceFlow({ className }) {
  const [active, setActive] = useState("control");

  const activeNode = useMemo(
    () => NODES.find((n) => n.key === active) ?? NODES[0],
    [active],
  );

  return (
    <div className={cn("grid gap-6 lg:grid-cols-2", className)}>
      <div className="glass-card relative overflow-hidden p-6">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "var(--gradient-card-hover)" }} />

        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Compliance Flow
          </div>
          <div className="mt-2 text-lg font-semibold text-text-primary">
            Regulation → Control → Technology → Evidence → Dashboard
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {NODES.map((n, idx) => (
              <button
                key={n.key}
                type="button"
                onClick={() => setActive(n.key)}
                className={cn(
                  "focus-ring flex items-center justify-between rounded-2xl border border-border-subtle bg-white/5 px-4 py-3 text-left transition-colors hover:bg-white/10",
                  active === n.key && "bg-white/10",
                )}
              >
                <div>
                  <div className="text-sm font-semibold text-text-primary">
                    {n.title}
                  </div>
                  <div className="text-sm text-text-secondary">{n.subtitle}</div>
                </div>
                <div className="text-text-muted">{idx < NODES.length - 1 ? "→" : "✓"}</div>
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-border-subtle bg-white/5 p-4">
            <div className="text-sm font-semibold text-text-primary">{activeNode.title}</div>
            <div className="mt-1 text-sm text-text-secondary">{activeNode.subtitle}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {activeNode.examples.map((e) => (
                <div
                  key={e}
                  className="rounded-full border border-border-subtle bg-white/5 px-3 py-1 text-xs font-semibold text-text-secondary"
                >
                  {e}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          Mapping view
        </div>
        <div className="mt-2 text-sm text-text-secondary">
          Regulation → Control Domain → Specific Control → Technology Category → Vendor/Tool → Evidence Type
        </div>

        <div className="mt-6 space-y-3">
          <div className="rounded-2xl border border-border-subtle bg-white/5 p-4">
            <div className="text-sm font-semibold text-text-primary">
              Example Path
            </div>
            <div className="mt-2 text-sm text-text-secondary">
              CERT-In → Log Retention → 180-day rolling logs → SIEM/Archive → Splunk + S3 → Retention configuration + retrieval proof
            </div>
          </div>

          <div className="rounded-2xl border border-border-subtle bg-white/5 p-4">
            <div className="text-sm font-semibold text-text-primary">
              Export
            </div>
            <div className="mt-2 text-sm text-text-secondary">
              PDF/PNG export will be wired in Part 3 with the full Technology Mapping page.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
